// Rocks kanban — five columns matching the planner_rock status enum.
// The quarter picker on the right cycles through the surrounding
// quarters; the kanban shows rocks dueAt-sorted within each column.
// Drag-and-drop across columns updates the rock's status via
// `setRockStatus`. Optimistic UI updates the local rock list
// immediately; on error we rollback by re-fetching from the server.
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { tpApi } from "../api";
import { useResource } from "../hooks";
import {
  currentQuarter,
  formatDateShort,
  isOverdue,
  nextQuarter,
  prevQuarter,
  statusChipClass,
} from "../utils";
import { Avatar, AvatarStack } from "../components/Avatar";
import { RockSheet } from "../sheets/RockSheet";
import { usePlannerCreate, usePlannerRefreshKey } from "../sheets/PlannerCreateContext";
import {
  PlannerListFilters,
  emptyPlannerFilters,
  type PlannerListFilterState,
} from "../components/PlannerListFilters";
import { PlannerSavedFilters } from "../components/PlannerSavedFilters";
import { ROCK_STATUS, type Rock } from "../../../../../../shared/team-planner/schemas";
import type { PlannerScopeId } from "../../../../../../shared/team-planner/const";

const COLUMNS: Array<{ id: Rock["status"]; label: string }> = [
  { id: "draft", label: "Draft" },
  { id: "on_track", label: "On track" },
  { id: "off_track", label: "Off track" },
  { id: "done", label: "Done" },
  { id: "missed", label: "Missed" },
];

function DroppableColumn({
  id,
  label,
  count,
  children,
}: {
  id: Rock["status"];
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`tp-kanban-column${isOver ? " tp-droppable-over" : ""}`}
    >
      <div className="tp-kanban-col-head">
        <span>{label}</span>
        <span className="tp-kanban-col-count">{count}</span>
      </div>
      {children}
    </div>
  );
}

function DraggableRockCard({
  rock,
  onOpen,
  showQuarter,
}: {
  rock: Rock;
  onOpen: (id: string) => void;
  showQuarter?: boolean;
}) {
  const { setNodeRef, listeners, attributes, isDragging, transform } = useDraggable({
    id: rock.id,
    data: { kind: "rock" },
  });
  // CSS transform during drag — the 6px sensor threshold means a
  // click below that distance leaves transform null, so a plain
  // click still opens the details sheet.
  const style: React.CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : {};
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={isDragging ? "tp-dragging" : undefined}
      style={style}
      // Open sheet on click — listeners + click coexist because the
      // sensor activation constraint requires 6px of movement before
      // a drag fires; under that threshold the synthetic click goes
      // through.
      onClick={(e) => {
        if (isDragging) return;
        e.stopPropagation();
        onOpen(rock.id);
      }}
    >
      <RockCard rock={rock} showQuarter={showQuarter} />
    </div>
  );
}

function RockCard({ rock, showQuarter }: { rock: Rock; showQuarter?: boolean }) {
  const overdue = isOverdue(rock.dueAt, rock.status);
  const milestonePct =
    rock.milestoneCounts.total === 0
      ? rock.progressPercent
      : Math.round((rock.milestoneCounts.done / rock.milestoneCounts.total) * 100);
  return (
    <div className="tp-card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
        <span className="tp-card-seq bsp-mono">R{rock.seq}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {showQuarter ? (
            <span
              className="bsp-mono"
              title={`Quarter ${rock.quarter}`}
              style={{
                font: "700 10px/1 var(--bsp-font-mono)",
                letterSpacing: "0.06em",
                color: "var(--bsp-fg-muted)",
                background: "var(--bsp-surface-muted)",
                border: "1px solid var(--bsp-border)",
                padding: "3px 6px",
                borderRadius: 4,
              }}
            >
              {rock.quarter}
            </span>
          ) : null}
          <span className={statusChipClass(rock.status)}>{rock.status.replace("_", " ")}</span>
        </span>
      </div>
      <div className="tp-card-title">{rock.title}</div>
      {rock.parentTeam ? (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            font: "700 9px/1 var(--bsp-font-sans)",
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: "var(--bsp-blue)",
          }}
        >
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: 3,
              background: "var(--bsp-blue-tint)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 8,
              fontWeight: 800,
            }}
          >
            {(rock.parentTeam.monogram ?? "··").toUpperCase()}
          </span>
          {rock.parentTeam.name}
        </span>
      ) : null}
      {milestonePct > 0 ? (
        <div className="tp-card-progress" aria-label={`Progress ${milestonePct}%`}>
          <div className="tp-card-progress-fill" style={{ width: `${milestonePct}%` }} />
        </div>
      ) : null}
      <div className="tp-card-meta">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ color: overdue ? "var(--bsp-red-fg)" : undefined }}>
            Due {formatDateShort(rock.dueAt)}
          </span>
          {rock.milestoneCounts.total > 0 ? (
            <span
              style={{
                color: "var(--bsp-fg-muted)",
                fontFamily: "var(--bsp-font-mono)",
                fontSize: 10,
              }}
            >
              · {rock.milestoneCounts.done}/{rock.milestoneCounts.total}
            </span>
          ) : null}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {rock.primaryOwner ? <Avatar owner={rock.primaryOwner} /> : null}
          {rock.watchers.length > 0 ? (
            <AvatarStack owners={rock.watchers} total={rock.watcherCount} max={2} />
          ) : null}
        </span>
      </div>
    </div>
  );
}

export function RocksView({ scope }: { scope: PlannerScopeId }) {
  const [quarter, setQuarter] = useState(currentQuarter());
  // When true, the API call drops the quarter filter and returns every
  // non-archived rock the viewer can see. Toggled by the "All" chip.
  const [allQuarters, setAllQuarters] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const openCreate = usePlannerCreate();
  const refreshKey = usePlannerRefreshKey();
  const [filters, setFilters] = useState<PlannerListFilterState>(emptyPlannerFilters);

  const rocks = useResource(
    () => tpApi.listRocks(allQuarters ? null : quarter, scope),
    [quarter, allQuarters, scope, refreshKey],
  );

  // Local override of rock statuses for optimistic DnD. Keyed by id;
  // a successful mutation invalidates the resource (clears the
  // override via refresh); a failure also clears it via the rollback
  // refresh.
  const [statusOverrides, setStatusOverrides] = useState<Map<string, Rock["status"]>>(new Map());
  useEffect(() => {
    // Wipe overrides on every refetch so the server is the source of
    // truth once it confirms.
    setStatusOverrides(new Map());
  }, [rocks.data]);

  const filtered = useMemo(() => {
    if (!rocks.data) return [];
    return rocks.data.filter((r) => {
      // Status filter applies a positive include-only test — empty
      // values list means no filter on that axis (everything passes).
      if (filters.status.length > 0 && !filters.status.includes(r.status)) return false;
      return true;
    });
  }, [rocks.data, filters]);

  const grouped = useMemo(() => {
    const map = new Map<Rock["status"], Rock[]>();
    for (const col of COLUMNS) map.set(col.id, []);
    for (const r of filtered) {
      // Optimistic override applies before the server confirms.
      const effectiveStatus = statusOverrides.get(r.id) ?? r.status;
      // "cancelled" rocks bucket into the Missed column so the kanban
      // doesn't lose them when the dropdown picks that terminal state.
      const target = effectiveStatus === "cancelled" ? "missed" : effectiveStatus;
      map.get(target)?.push(r);
    }
    return map;
  }, [filtered, statusOverrides]);

  // 6px drag threshold — matches Drillcut RocksKanban.tsx:138 so a
  // plain click on a card still opens the details sheet.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  async function onDragEnd(e: DragEndEvent) {
    const rockId = String(e.active.id);
    const newStatus = e.over?.id ? String(e.over.id) as Rock["status"] : null;
    if (!newStatus) return;
    const rock = filtered.find((r) => r.id === rockId);
    if (!rock) return;
    if (rock.status === newStatus) return;
    // Optimistic — paint the destination column immediately.
    setStatusOverrides((m) => {
      const next = new Map(m);
      next.set(rockId, newStatus);
      return next;
    });
    try {
      await tpApi.setRockStatus(rockId, newStatus);
      rocks.refresh();
    } catch {
      // Rollback by reverting + re-fetching from the server.
      setStatusOverrides((m) => {
        const next = new Map(m);
        next.delete(rockId);
        return next;
      });
      rocks.refresh();
    }
  }

  return (
    <>
      <div className="tp-toolbar">
        <div className="tp-toolbar-left">
          {!allQuarters ? (
            <>
              <button
                type="button"
                className="bsp-btn bsp-btn-secondary bsp-btn-icon"
                onClick={() => setQuarter(prevQuarter(quarter))}
                aria-label="Previous quarter"
              >
                <ChevronLeft size={16} />
              </button>
              <div
                className="bsp-mono"
                style={{
                  padding: "8px 12px",
                  border: "1px solid var(--bsp-border)",
                  borderRadius: 4,
                  background: "var(--bsp-surface)",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                }}
              >
                {quarter}
              </div>
              <button
                type="button"
                className="bsp-btn bsp-btn-secondary bsp-btn-icon"
                onClick={() => setQuarter(nextQuarter(quarter))}
                aria-label="Next quarter"
              >
                <ChevronRight size={16} />
              </button>
            </>
          ) : null}
          <button
            type="button"
            className={`bsp-btn bsp-btn-sm ${allQuarters ? "bsp-btn-primary" : "bsp-btn-secondary"}`}
            onClick={() => setAllQuarters((v) => !v)}
            aria-pressed={allQuarters}
            title={
              allQuarters
                ? "Showing every non-archived rock. Click to return to a single quarter."
                : "Show every non-archived rock across all quarters."
            }
          >
            All
          </button>
        </div>
        <div className="tp-toolbar-right">
          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            onClick={() => openCreate("rock", { quarter })}
          >
            <Plus size={14} /> New rock
          </button>
        </div>
      </div>

      <PlannerListFilters
        state={filters}
        onChange={setFilters}
        statusOptions={ROCK_STATUS.map((s) => ({ id: s, label: s.replace("_", " ") }))}
      />
      <PlannerSavedFilters view="rocks" current={filters} onApply={setFilters} />

      {rocks.error ? (
        <p style={{ color: "var(--bsp-red-fg)" }}>Failed to load rocks: {rocks.error}</p>
      ) : null}

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="tp-kanban">
          {COLUMNS.map((col) => {
            const items = grouped.get(col.id) ?? [];
            return (
              <DroppableColumn key={col.id} id={col.id} label={col.label} count={items.length}>
                {items.length === 0 ? (
                  <div className="tp-empty">No rocks</div>
                ) : (
                  items.map((r) => (
                    <DraggableRockCard
                      key={r.id}
                      rock={r}
                      onOpen={setOpenId}
                      showQuarter={allQuarters}
                    />
                  ))
                )}
              </DroppableColumn>
            );
          })}
        </div>
      </DndContext>

      {openId ? (
        <RockSheet
          id={openId}
          quarter={quarter}
          onClose={() => setOpenId(null)}
          onChanged={() => rocks.refresh()}
        />
      ) : null}
    </>
  );
}
